import { TCustomerField } from 'modules/checkout/domain/entities';

export type TVectisMissingFieldProfile = 'CLIENTE' | 'RESPONSAVEL';

export interface IVectisMissingCustomerField {
  perfil: TVectisMissingFieldProfile;
  campo: string;
}

/**
 * O Vectis nomeia os campos como eles aparecem nas informações básicas do cliente; a tela os
 * conhece pelo caminho do formulário. Este é o único ponto que traduz um no outro.
 *
 * `numeroRg` e `numeroDocumentoFiscal` chegam separados e assim permanecem. São colunas
 * diferentes no cadastro — o Brasil usa a primeira, o Paraguai a segunda — e tratá-las como uma
 * só fazia o site gravar o mesmo valor nas duas.
 */
const FIELD_BY_PROFILE: Record<
  TVectisMissingFieldProfile,
  Partial<Record<string, TCustomerField>>
> = {
  CLIENTE: {
    dataNascimento: 'birthdate',
    email: 'email',
    cpf: 'document',
    numeroRg: 'rgDocument',
    numeroDocumentoFiscal: 'taxDocument',
    foneCelular: 'phone',
    numeroCep: 'address.postalCode',
    idCidade: 'address.city.id',
    idBairro: 'address.district.id',
    logradouro: 'address.street',
    numeroLogradouro: 'address.number',
  },
  RESPONSAVEL: {
    nome: 'guardian.firstName',
    sobrenome: 'guardian.lastName',
    dataNascimento: 'guardian.birthdate',
    cpf: 'guardian.document',
    numeroRg: 'guardian.rgDocument',
    numeroDocumentoFiscal: 'guardian.taxDocument',
    foneCelular: 'guardian.phone',
    numeroCep: 'guardian.address.postalCode',
    idCidade: 'guardian.address.city.id',
    idBairro: 'guardian.address.district.id',
    logradouro: 'guardian.address.street',
    numeroLogradouro: 'guardian.address.number',
  },
};

/**
 * Traduz a lista do Vectis para os caminhos do formulário.
 *
 * Par que não está no mapa é descartado, não repassado. O formulário monta os inputs cruzando a
 * lista com os que ele sabe desenhar: um caminho sem input viraria um campo invisível que o
 * cliente não consegue preencher, com o botão travado até ele desistir. Prefere-se pedir de
 * menos — o Vectis ainda recusa a conclusão — a travar a tela.
 */
export function buildMissingCustomerFields(
  vectisFields?: IVectisMissingCustomerField[] | null,
): TCustomerField[] {
  if (!Array.isArray(vectisFields)) return [];

  const fields = vectisFields
    .map(({ perfil, campo }) => FIELD_BY_PROFILE[perfil]?.[campo])
    .filter((field): field is TCustomerField => !!field);

  return Array.from(new Set(fields));
}
